/**
 * Client → `/api/track-funnel-event` mirror helper.
 *
 * Uses absolute URL + fetch `keepalive` so the request survives tab close.
 * If fetch fails synchronously/async, falls back to `navigator.sendBeacon` with
 * a JSON blob (unload edge cases in some Safari / Instagram WebView builds).
 *
 * Edge: when traffic hits the Cloudflare Worker first, geo + CF-Ray / colo on the API
 * come from `request.cf` headers forwarded to origin — no extra client fields needed.
 */

import { BRAND_SITE_HOST } from "@/lib/brandConfig";
import { trafficSourceFunnelProps } from "@/lib/detectTrafficSource";
import {
  generateMetaEventId,
  getFbpFbcCookies,
  trackMetaEvent,
} from "@/lib/facebookPixel";
import { mapFunnelEventToMetaPixel } from "@/lib/funnelEventToMetaPixel";
import {
  buildCanonicalPostHogReplayUrl,
  captureFunnelMirrorPayloadPostHog,
  getPostHogCorrelationIdsPostHog,
  getPostHogRequestHeadersPostHog,
  isCanonicalPostHogReplayUrl,
} from "@/lib/posthogAnalytics";

export type MirrorFunnelEventOptions = {
  /** When false, only mirrors to `/api/track-funnel-event` (caller captures PostHog separately). Default true. */
  capturePosthog?: boolean;
  /**
   * When false, no dispara Meta Pixel client-side ni inyecta `meta_event_id`/`fbp`/`fbc`.
   * Útil cuando el caller ya disparó el evento Meta a mano y solo quiere mirrorear a Slack/DB.
   * Default true.
   */
  fireMetaPixel?: boolean;
  /**
   * Override del `event_id` para dedup con Conversions API. Si no se pasa, se genera uno.
   * Usar cuando el caller tiene un identificador natural (ej. `payment_intent.id` para Purchase).
   */
  metaEventId?: string;
};

/** Merge PostHog + brand context into `props` for Slack / admin (fills only missing keys). */
function withMirrorContext(props: Record<string, unknown> | undefined): Record<string, unknown> {
  const base =
    props && typeof props === "object" && !Array.isArray(props) ? { ...props } : {};

  const fill = (key: string, value: unknown) => {
    if (value === undefined || value === null || value === "") return;
    if (base[key] === undefined || base[key] === null || base[key] === "") {
      base[key] = value;
    }
  };

  try {
    const ph = (
      window as unknown as {
        posthog?: { get_session_id?: () => string; version?: string };
      }
    ).posthog;
    fill("posthog_client_session_id", ph?.get_session_id?.());
    fill("posthog_js_version", ph?.version);
  } catch {
    /* ignore */
  }

  try {
    const corr = getPostHogCorrelationIdsPostHog();
    fill("ph_distinct_id", corr.ph_distinct_id);
    // Force overwrite non-canonical replay URLs (/ingest/…, phc_…, or fake
    // /replay/{uuid-v4} from DualCTA flow_trace). Never keep dead links.
    const existing = base.ph_session_replay_url;
    if (!isCanonicalPostHogReplayUrl(typeof existing === "string" ? existing : null)) {
      const sid =
        (typeof base.posthog_client_session_id === "string" &&
          base.posthog_client_session_id) ||
        null;
      const built =
        corr.ph_session_replay_url || buildCanonicalPostHogReplayUrl(sid) || null;
      if (built && isCanonicalPostHogReplayUrl(built)) {
        base.ph_session_replay_url = built;
      } else {
        // Prefer no link over a 404 in Slack
        delete base.ph_session_replay_url;
      }
    }
  } catch {
    /* ignore */
  }

  // First-touch traffic source en CADA evento: de dónde vino el visitante
  // (Instagram link-in-bio, FB ad, TikTok orgánico, UTM, click_id…). Sin
  // esto Slack solo veía el header `referer` crudo — vacío en in-app browsers.
  try {
    const ts = trafficSourceFunnelProps();
    for (const [k, v] of Object.entries(ts)) fill(k, v);
  } catch {
    /* ignore */
  }

  fill("brand_host", BRAND_SITE_HOST);
  fill("build_mode", import.meta.env.MODE);

  return base;
}

/** Top-level ids for DB + Slack when callers only sent props (helps sendBeacon fallback). */
function withPostHogTopLevelIds(payload: Record<string, unknown>): Record<string, unknown> {
  const out = { ...payload };
  try {
    const ph = (
      window as unknown as {
        posthog?: { get_distinct_id?: () => string; get_session_id?: () => string };
      }
    ).posthog;
    if (
      (out.posthog_distinct_id === undefined ||
        out.posthog_distinct_id === null ||
        String(out.posthog_distinct_id).trim() === "") &&
      ph?.get_distinct_id
    ) {
      const id = ph.get_distinct_id();
      if (id) out.posthog_distinct_id = id;
    }
    if (
      (out.session_id === undefined ||
        out.session_id === null ||
        String(out.session_id).trim() === "") &&
      ph?.get_session_id
    ) {
      const sid = ph.get_session_id();
      if (sid) out.session_id = sid;
    }
  } catch {
    /* ignore */
  }
  return out;
}

/**
 * Inyecta `meta_event_id`, `fbp`, `fbc` en `props` y dispara el evento
 * estándar Meta Pixel client-side con `eventID` para dedup CAPI server-side.
 * Devuelve `props` mutado/enriquecido.
 */
function withMetaPixelDispatch(
  eventName: string,
  props: Record<string, unknown>,
  options?: MirrorFunnelEventOptions,
): Record<string, unknown> {
  const out = { ...props };

  // `meta_event_id`: ID compartido entre client (fbq) y server (CAPI).
  const incoming = typeof out.meta_event_id === "string" ? out.meta_event_id : "";
  const metaEventId = options?.metaEventId || incoming || generateMetaEventId();
  out.meta_event_id = metaEventId;

  // `_fbp`/`_fbc` — los necesita el server-side para attribution. Inyectarlos
  // en cada evento (overhead mínimo) en lugar de en una sola llamada.
  try {
    const { fbp, fbc } = getFbpFbcCookies();
    if (fbp && out.fbp === undefined) out.fbp = fbp;
    if (fbc && out.fbc === undefined) out.fbc = fbc;
  } catch {
    /* noop */
  }

  if (options?.fireMetaPixel === false) return out;

  // Mapear evento del funnel → evento estándar Meta.
  const mapped = mapFunnelEventToMetaPixel(eventName, out);
  if (mapped) {
    try {
      trackMetaEvent(mapped.eventName, mapped.params, { eventID: metaEventId });
    } catch {
      /* noop */
    }
  }

  return out;
}

export function mirrorFunnelEvent(
  payload: Record<string, unknown>,
  options?: MirrorFunnelEventOptions,
): void {
  if (typeof window === "undefined") return;

  const eventName = typeof payload.event_name === "string" ? payload.event_name : "";

  const propsWithMirror =
    "props" in payload
      ? withMirrorContext(payload.props as Record<string, unknown> | undefined)
      : withMirrorContext(undefined);

  // Inyecta meta_event_id + fbp/fbc + dispara el pixel client-side.
  const propsWithPixel = eventName
    ? withMetaPixelDispatch(eventName, propsWithMirror, options)
    : propsWithMirror;

  const enrichedBase = { ...payload, props: propsWithPixel };
  const enriched = withPostHogTopLevelIds(enrichedBase);

  if (options?.capturePosthog !== false) {
    captureFunnelMirrorPayloadPostHog(enriched);
  }

  const body = JSON.stringify(enriched);
  const url = `${window.location.origin}/api/track-funnel-event`;

  try {
    void fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...getPostHogRequestHeadersPostHog(),
      },
      keepalive: true,
      body,
    }).catch(() => {
      try {
        if (typeof navigator.sendBeacon === "function") {
          navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
        }
      } catch {
        /* noop */
      }
    });
  } catch {
    try {
      if (typeof navigator.sendBeacon === "function") {
        navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
      }
    } catch {
      /* noop */
    }
  }
}
